import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormGroupDirective} from '@angular/forms';
import {ClearanceStatus, TaskMessageStatus} from 'typescript-models/common';
import {EmergencySummaryExpanded} from '../../emergency-overview-item/emergency-overview-item.component';
import {EmergencyOverviewUtils} from '../../emergency-overview.utils';

@Component({
  selector: 'app-operational-port-authority-editor',
  templateUrl: './operational-port-authority-editor.component.html',
  styleUrl: './operational-port-authority-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OperationalPortAuthorityEditorComponent implements OnInit {
  protected readonly form = inject(FormGroupDirective);
  private readonly destroyRef = inject(DestroyRef);

  readonly summary = input<EmergencySummaryExpanded>();
  readonly isIncomingTrip = input<boolean>(false);
  readonly isOutgoingTrip = input<boolean>(false);
  // When true the caller is a pure LW (pilot) who may only edit the LW-allowed fields; MH-only controls
  // (declaration statuses, operational contact, current vessel draft, tidal window, on hold) are hidden.
  readonly isPilotOnly = input<boolean>(false);

  protected readonly currentVesselDraught = computed(() =>
    this.summary() ? EmergencyOverviewUtils.getAgentReportedVesselDraught(this.summary()) : null);

  protected readonly maxVesselDraught = computed(() => this.summary()?.vessel?.maxDraught ?? null);

  protected readonly portAuthorityValues = signal<OperationPortAuthorityValues | null>(null);

  // Vessel draft is always relevant for a shifting/outgoing trip. For an incoming trip
  // it's only relevant once there is operational contact with the vessel.
  protected readonly showVesselDraft = computed(() =>
    this.isOutgoingTrip() || this.isShifting() || (this.isIncomingTrip() && !!this.portAuthorityValues()?.operationalContact));

  protected readonly isShifting = computed(() =>
    !this.isOutgoingTrip() && !this.isIncomingTrip());

  protected readonly clearanceStatusOptions = [
    ClearanceStatus.PENDING,
    ClearanceStatus.APPROVED,
    ClearanceStatus.APPROVED_WITH_REMARKS,
    ClearanceStatus.REJECTED,
  ];

  private readonly clearanceKeys = ['ekh', 'mdoh', 'sspi', 'emas', 'emgs'] as const;

  protected readonly booleanOptions = [
    true,
    false
  ];
  protected readonly showShoreBasedPilotage = computed(() =>
    this.summary()?.incomingMovementRemotePilotage);

  ngOnInit(): void {
    this.portAuthorityValues.set(
      this.form.control.get("portAuthority")?.value
    );
    this.form.control.get("portAuthority")?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.portAuthorityValues.set(value);
      this.clearStaleClearanceRemarks(value);
    });
  }

  private clearStaleClearanceRemarks(value: OperationPortAuthorityValues | null): void {
    const clearances = value?.clearances;
    if (!clearances) {
      return;
    }
    const clearancesGroup = this.form.control.get('portAuthority')?.get('clearances');
    for (const key of this.clearanceKeys) {
      const status = clearances[key]?.status;
      const showRemarks = status === ClearanceStatus.REJECTED || status === ClearanceStatus.APPROVED_WITH_REMARKS;
      if (showRemarks) {
        continue;
      }
      const clearanceGroup = clearancesGroup?.get(key);
      if (clearances[key]?.remarks != null) {
        const remarksControl = clearanceGroup?.get('remarks');
        remarksControl?.setValue(null);
        remarksControl?.markAsPristine();
      }
      const attentionPointsArray = clearanceGroup?.get('attentionPoints') as FormArray | null;
      if (attentionPointsArray && attentionPointsArray.length > 0) {
        attentionPointsArray.clear();
        attentionPointsArray.markAsPristine();
      }
    }
  }

  protected actualVesselDraftChanged(value: Number): void {
    const formControl = this.form.control.get('portAuthority')?.get('actualVesselDraft');
    formControl?.setValue(value);
    formControl?.markAllAsDirty();
  }

  protected booleanFormatter(value: boolean) {
    if (value) {
      return "Yes";
    } else {
      return "No"
    }
  }

  protected clearanceToMessageStatus(status: ClearanceStatus): TaskMessageStatus {
    switch (status) {
      case null:
      case undefined:
      case ClearanceStatus.UNKNOWN:
      case ClearanceStatus.PENDING:
        return TaskMessageStatus.UNKNOWN;
      case ClearanceStatus.APPROVED_WITH_REMARKS:
        return TaskMessageStatus.ACCEPTED_WITH_CONDITIONS;
      case ClearanceStatus.APPROVED:
        return TaskMessageStatus.ACCEPTED;
      case ClearanceStatus.REJECTED:
        return TaskMessageStatus.REJECTED;
      default:
        return TaskMessageStatus.DISABLED;
    }
  }
}

interface OperationPortAuthorityValues {
  clearances: OperationPortAuthorityClearances;
  operationalContact: boolean;
  actualVesselDraft: number;
  tidalWindow: boolean;
  tidalWindowStart: string;
  tidalWindowEnd: string;
  pilotFromDistance: boolean;
  pilotFromDistanceAccepted: boolean;
  onHold: boolean;
  onHoldDate: string;
}

interface OperationPortAuthorityClearance {
  status: ClearanceStatus;
  remarks?: string;
  attentionPoints?: { type?: string; remarks?: string }[];
}

interface OperationPortAuthorityClearances {
  ekh: OperationPortAuthorityClearance;
  mdoh: OperationPortAuthorityClearance;
  sspi: OperationPortAuthorityClearance;
  emas: OperationPortAuthorityClearance;
  emgs: OperationPortAuthorityClearance;
}
